<template>
    <custom-table
        :title="$t('pages.ecommerce.product.title')"
        :subTitle="$t('pages.ecommerce.product.subTitle')"
        rowKey="id"
        :items="table.data"
        :loading="table.loading"
        selectableRows
        :columns="fields"
        :actions="actions"
        :pagination="table.pagination"
        :sortMeta="table.pagination.sort || []"
        changeUrlPage
        filterComponentName="ProductFilter"
        @action="handleClickAction"
        @selectableRows="handleSelectedRow"
        @changeTable="handleTableChange"
        @sort="handleSort"
    >
        <template v-slot:id="{ row: record }">
            {{ record.id }}
            <span v-if="record.deleted_at" class="badge badge-light-danger ms-1">{{ $t('common.deleted') }}</span>
        </template>
        <template v-slot:product_type="{ row: record }">
            {{ record.product_type_id ? $t('pages.ecommerce.product.types[' + (record.product_type_id - 1) + ']') : '-' }}
        </template>
        <template v-slot:target_type="{ row: record }">
            {{ record.target_type === 'CUSTOMER' ? $t('common.customer') : $t('common.company') }}
        </template>
        <template v-slot:active="{ row: record }">
            <span :class="record.active ? 'badge-light-success': 'badge-light-danger'" class="badge">{{ record.active ? $t('common.active') : $t('common.passive') }}</span>
        </template>
        <template v-slot:created_at="{ row: record }">
            {{ record.created_at ? $moment(record.created_at).format("DD.MM.YYYY HH:mm") : '-' }}
        </template>
        <template v-slot:actions="{ row: record }">
            <div class="d-flex justify-content-end">
                <el-tooltip :content="$t('common.edit')" placement="top">
                    <router-link :to="'/ecommerce/product/save/' + record.id" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                     <span class="svg-icon svg-icon-3">
                         <inline-svg src="/media/icons/duotune/art/art005.svg"/>
                     </span>
                    </router-link>
                </el-tooltip>
                <a @click="deleteRecord([record.id])" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                    <span class="svg-icon svg-icon-3">
                        <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                    </span>
                </a>
            </div>
        </template>
    </custom-table>
</template>

<script>
import CustomTable from "@/components/custom-table";

export default {
    name: "ProductIndex",
    components: {
        CustomTable
    },
    data() {
        return {
            fields: [
                {
                    name: this.$t("pages.ecommerce.product.cols.id"),
                    key: "id",
                    sortable: true,
                    scopedSlots: {customRender: "id"}
                },
                {
                    name: this.$t("pages.ecommerce.product.cols.name"),
                    key: "name",
                    sortable: true,
                },
                {
                    name: this.$t("pages.ecommerce.product.cols.code"),
                    key: "code",
                    sortable: true,
                },
                {
                    name: this.$t("pages.ecommerce.product.cols.product_type"),
                    sortingField: "product_type_id",
                    sortable: true,
                    scopedSlots: {customRender: "product_type"}
                },
                {
                    name: this.$t("pages.ecommerce.product.cols.target_type"),
                    sortingField: "target_type",
                    sortable: true,
                    scopedSlots: {customRender: "target_type"}
                },
                {
                    name: this.$t("common.sort"),
                    key: "sort",
                    sortable: true,
                },
                {
                    name: this.$t("pages.ecommerce.product.cols.active"),
                    sortingField: "active",
                    sortable: true,
                    scopedSlots: {customRender: "active"}
                },
                {
                    name: this.$t("common.createdAt"),
                    sortingField: "created_at",
                    sortable: true,
                    scopedSlots: {customRender: "created_at"}
                },
                {
                    name: this.$t("common.action"),
                    key: "action",
                    scopedSlots: {customRender: "actions"}
                }
            ],
            actions: [
                {
                    name: "new",
                    icon: "bi-plus-lg",
                    label: "btn.add"
                },
                {
                    name: "refresh",
                    icon: "bi-arrow-repeat",
                    label: "btn.refresh"
                },
                {
                    name: "delete",
                    icon: "bi-trash",
                    label: "btn.delete"
                }
            ],
            selectedRowKeys: [],
            filterParameters: {},
            exportLoading: false
        }
    },
    computed: {
        table() {
            return this.$store.state.ecommerce.product.table;
        },
        userID() {
            return this.$route.query.userID;
        }
    },
    mounted() {
        this.setCurrentPageBreadcrumbs(this.$t("menu.productList"), []);

        this.filterParameters = {
            sort: 'created_at:desc'
        };

        this.$store.dispatch('ecommerce/product/get', {
            page: {
                current: this.$route.query.page
            },
            filterData: Object.assign(this.filterParameters, this.$root.getFilterWithUrl({}))
        });
    },
    methods: {
        handleClickAction(name) {
            switch (name) {
                case "refresh":
                    this.refreshTable();
                    break;

                case "new":
                    this.$router.push({
                        path: "/ecommerce/product/save"
                    });
                    break;

                case "delete":
                    this.deleteRecord(this.selectedRowKeys.flat());
                    break;

                default:
                    break;
            }
        },
        refreshTable() {
            this.$store.dispatch('ecommerce/product/get', {
                page: {
                    current: this.$route.query.page
                },
                filterData: Object.assign(this.filterParameters, this.$root.getFilterWithUrl({}))
            });
        },
        handleTableChange(pagination, filterData) {
            const currentSort = this.filterParameters.sort || 'created_at:desc';
            this.filterParameters = {...filterData, sort: currentSort};

            this.$store.dispatch("ecommerce/product/get", {
                page: pagination,
                filterData: this.filterParameters
            });
        },
        handleSort({columnName, order}) {
            this.filterParameters = {
                ...this.filterParameters,
                sort: `${columnName}:${order}`
            };

            this.$store.dispatch("ecommerce/product/get", {
                page: {current: this.$route.query.page},
                filterData: this.filterParameters
            });
        },
        handleSelectedRow(record) {
            this.selectedRowKeys = record;
        },
        deleteRecord(id) {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('btn.delete'),
                message: this.$t('messages.sureDelete'),
                confirmType: 'danger',
                onConfirm: () => {
                    this.$store.dispatch("ecommerce/product/delete", {id}).then((successDeleted) => {
                        this.selectedRowKeys = this.selectedRowKeys.filter(x => !successDeleted.includes(x));
                        this.refreshTable();
                    });
                },
            });
        },
    }
}
</script>

<style scoped>

</style>